import {
    AdditionalCostCorrectionDetailsDto,
    AdditionalCostDetailsDto,
    ChildId,
    CostAllocationRefersTo,
} from '../../../../../../types';
import {
    AppCardListItem,
    AppTitle,
    DetailsColumn,
    DetailsFrameBase,
    DetailsRow,
} from '../../../../../atoms';
import { AppCardList } from '../../../../../organisms';
import { settlementTexts } from '../../../../../../consts/text';
import { getFormattedPrice } from '../../../../../../utils/getFormattedPrice';
import { payerMapper } from '../../../../../../consts/settlementChildren/payerMapper';
import { referToMapper } from '../../../../../../consts/settlementChildren/referToMapper';
import { If } from '../../../../../atoms/If/If';
import { useIsMobile } from '../../../../../../hooks/useIsMobile/useIsMobile';
import { ParentCorrectionInvoices } from '../../../ParentCorrectionInvoices/ParentCorrectionInvoices';
import { AdditionalCostInvoice } from '../../../ChildSettlementDetails/AdditionalCostList/AdditionalCostItem/AdditionalCostDetails/AdditionalCostInvoice';

type Props = {
    childId: ChildId;
    correction: AdditionalCostCorrectionDetailsDto;
    detailsDto: AdditionalCostDetailsDto;
    index: number;
};

export const ParentAdditionalCostCorrectionItem = ({
    childId,
    correction,
    detailsDto,
    index,
}: Props) => {
    const correctionCostSplits = correction?.costSplits;
    const isMobile = useIsMobile();

    if (!correction) {
        return null;
    }

    return (
        <DetailsFrameBase key={correction.id}>
            <AppTitle level={4} marginTop={-16} marginBottom={16}>
                {`${
                    correction.verified ? 'Korekta' : 'Propozycja korekty'
                } ${correction?.orderNumber}: ${detailsDto.name}`}
            </AppTitle>
            <AppCardListItem key={correction.id} index={index} listItem={undefined}>
                <DetailsRow>
                    <DetailsColumn
                        title={settlementTexts.children.manualBill.settlementDetails.newAmount}
                        span={6}
                    >
                        {correction.grossSummary ? `${correction.grossSummary} zł` : '-'}
                    </DetailsColumn>
                    <DetailsColumn
                        title={
                            settlementTexts.children.manualBill.settlementDetails.amountDifference
                        }
                        span={6}
                    >
                        {correction.grossSummary ? `${correction.grossAmountDifference} zł` : '-'}
                    </DetailsColumn>
                </DetailsRow>
            </AppCardListItem>
            <ParentCorrectionInvoices
                fileName={correction.fileName ?? `Faktura korygująca - ${detailsDto.name}`}
                title={`Faktura dla ${correction.verified ? '' : 'propozycji'} korekty ${
                    correction.orderNumber
                }`}
                correctionId={correction.id}
            />
            <AppCardList
                marginTop={16}
                title="Podział kosztów"
                dataSource={correctionCostSplits ?? []}
                isHiddenAddButton
                emptyInfo={settlementTexts.children.costAllocation.emptyInfo}
                renderItem={(record, idx) => {
                    return (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: 16,
                                alignItems: 'flex-start',
                                width: '100%',
                            }}
                        >
                            <div style={{ flex: 1 }}>
                                <AppCardListItem key={correction.id} index={idx} listItem={record}>
                                    <DetailsRow>
                                        <DetailsColumn
                                            title={
                                                settlementTexts.children.costSplit.institutionName
                                            }
                                            span={16}
                                        >
                                            {record.foundationName}
                                        </DetailsColumn>
                                    </DetailsRow>
                                    <DetailsRow>
                                        <DetailsColumn
                                            title={settlementTexts.children.costSplit.nip}
                                            span={6}
                                        >
                                            {record.nip}
                                        </DetailsColumn>
                                        <DetailsColumn
                                            title={
                                                settlementTexts.children.costSplit.percentageAmount
                                            }
                                            span={6}
                                        >
                                            {record.percentageAmount
                                                ? `${record.percentageAmount} %`
                                                : '-'}
                                        </DetailsColumn>
                                        <DetailsColumn
                                            title={settlementTexts.children.costSplit.finalAmount}
                                            span={6}
                                        >
                                            {getFormattedPrice(record.finalGrossAmount || 0)}
                                        </DetailsColumn>

                                        <DetailsColumn
                                            title={settlementTexts.children.costSplit.payerType}
                                            span={6}
                                        >
                                            {payerMapper[record.payerType]}
                                        </DetailsColumn>
                                    </DetailsRow>
                                    <DetailsRow>
                                        <DetailsColumn
                                            title={settlementTexts.children.costSplit.refersTo}
                                            span={6}
                                        >
                                            {referToMapper[record.costCategory]}
                                        </DetailsColumn>
                                        <If
                                            condition={
                                                record.costCategory ===
                                                CostAllocationRefersTo.EXTRA_LESSONS
                                            }
                                        >
                                            <DetailsColumn
                                                title={settlementTexts.children.costSplit.courses}
                                                span={6}
                                            >
                                                {record?.courseList
                                                    ?.map((course) => course.courseName)
                                                    .join(', ') ?? ''}
                                            </DetailsColumn>
                                        </If>
                                        <DetailsColumn
                                            title={settlementTexts.children.costSplit.invoiceNotes}
                                            span={12}
                                        >
                                            {record.invoiceNotes}
                                        </DetailsColumn>
                                    </DetailsRow>
                                </AppCardListItem>
                            </div>

                            {record.invoiceData && (
                                <div
                                    style={{
                                        width: isMobile ? '100%' : 320,
                                        flexShrink: 0,
                                    }}
                                >
                                    <AdditionalCostInvoice
                                        childId={childId}
                                        invoiceData={record.invoiceData}
                                        isParent
                                        title="Rozliczenie"
                                    />
                                </div>
                            )}
                        </div>
                    );
                }}
            />
        </DetailsFrameBase>
    );
};
